/**
 * Network utility functions for handling offline/online states
 */

/**
 * Checks if the device has network connectivity
 * This is more reliable than just navigator.onLine
 */
export async function isNetworkAvailable(): Promise<boolean> {
  // First check navigator.onLine
  if (!navigator.onLine) {
    return false;
  }

  // Try to make a HEAD request to a reliable endpoint
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    await fetch('https://www.google.com/favicon.ico', {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return true;
  } catch {
    // Network request failed
    return false;
  }
}

/**
 * Check if a specific API endpoint is reachable
 */
export async function isApiReachable(baseUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${baseUrl}/health`, {
      method: 'HEAD',
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response.ok || response.status === 404; // 404 means server is reachable
  } catch (error) {
    console.log('API not reachable:', error);
    return false;
  }
}

/**
 * Detects if running on iOS/iPad
 */
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

/**
 * Detects if running in standalone mode (installed as PWA)
 */
export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator as any).standalone === true;
}
